import React, { Suspense, lazy, useContext, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Tab, Tabs } from '@mui/material';
import AuthContext from '../../context/authContext';
import { viewRootTextPrimarySx } from '../../util/chartTheme';
import AreaStaffCostStatistics from './AreaStaffCostStatistics';
import ClusterHealth from './ClusterHealth';
import FleetOverview from './FleetOverview';
import PrinterOverview from './PrinterOverview';

// Build-safe optional dependency: the issue/outage-based IT statistics view
// (reaction times, MTTR, downtime, escalations) only exists on branches that
// ship the "Mängel & Störungen" issue-tracking feature. import.meta.glob
// resolves to an empty object where the file is absent, so the production build
// on master — which has no issue feature — still compiles. Where present, the
// view is lazy-loaded as the "Störungen" tab so all IT statistics live in a
// single place under "Statistiken".
const issueStatModules = import.meta.glob('./ItStatisticsView.jsx');
const IssueOperationsStatistics = issueStatModules['./ItStatisticsView.jsx']
  ? lazy(issueStatModules['./ItStatisticsView.jsx'])
  : null;

// Combined IT statistic: issue/outage operations, IT personnel cost and the
// IT-monitoring dashboards (Cluster Health, device fleet, printers) under one
// "Statistiken" entry. Each tab is gated by its own right and only mounted
// while active.
const IT_TABS = [
  ...(IssueOperationsStatistics
    ? [
        {
          key: 'operations',
          label: 'Störungen',
          rights: ['issue:statistics', 'issue'],
          render: () => (
            <Suspense
              fallback={
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              }
            >
              <IssueOperationsStatistics />
            </Suspense>
          ),
        },
      ]
    : []),
  {
    key: 'staff',
    label: 'Personalkosten',
    rights: ['statistic:special:staffCost'],
    render: () => <AreaStaffCostStatistics presetArea='IT' />,
  },
  {
    key: 'cluster',
    label: 'Cluster Health',
    rights: ['cluster:read'],
    render: () => <ClusterHealth />,
  },
  {
    key: 'fleet',
    label: 'Geräte-Fleet',
    rights: ['fleet:read', 'fleet:read:own'],
    render: () => <FleetOverview />,
  },
  {
    key: 'printer',
    label: 'Drucker',
    rights: ['printerStatus:read', 'printerStatus:read:own'],
    render: () => <PrinterOverview />,
  },
];

const ITStatistics = () => {
  const authCtx = useContext(AuthContext);
  const tabs = useMemo(
    () => IT_TABS.filter(t => t.rights.some(r => authCtx.checkRight(r))),
    [authCtx]
  );
  const [active, setActive] = useState(0);

  if (tabs.length === 0) return <Navigate to='/welcome' replace />;

  const safeActive = Math.min(active, tabs.length - 1);
  const ActiveTab = tabs[safeActive];

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1, sm: 2 } }}>
        <Tabs
          value={safeActive}
          onChange={(_, v) => setActive(v)}
          variant='scrollable'
          scrollButtons='auto'
          allowScrollButtonsMobile
        >
          {tabs.map(t => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
      </Box>
      <Box>{ActiveTab.render()}</Box>
    </Box>
  );
};

export default ITStatistics;
